import MomentUtils from '@date-io/moment';
import { SvgIcon, Typography, useMediaQuery } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { ThemeProvider } from '@material-ui/core/styles';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { getRegisterTouchpointGreetings } from 'src/touchpoint/helper/touchpoint-register-helper';
import { LiveWeightArea } from '../common/components/live-weight-area/live-weight-area';
import { userLockStatus } from '../common/enumerations/user-lock-status';
import XLRLogger from '../common/helpers/logger';
import { ILoginUser } from '../common/interfaces/dtos/ILoginUser';
import { ILoginUserErrors } from '../common/interfaces/dtos/ILoginUserErrors';
import { IShowPassword } from '../common/interfaces/dtos/IShowPassword';
import { IButton } from '../common/interfaces/IButton';
import { store } from '../common/redux/configure-store';
import { CssTextField } from '../css-text-field/css-text-field';
import { getLatestTransaction } from '../home-page/business-logic/get-latest-transanction';
import {
  datetimeTheme,
  newStyles as loginStyles,
} from '../touchpoint/components/login-emerald-styles';
import timeIcon from './media/time-picker-logo.svg';
import { useTranslation } from 'react-i18next';
import { LocalStorage } from '../common/helpers/local-storage';
import {
  getMetaData,
  useIsEmeraldSellingService,
} from 'src/xlr-configuration/pos-service-type-utils';
import IAMLoginButton from './iam-login-button';

const futureDateRange = 14;
const getDateLimit = (): Date => {
  const CurrentDate = new Date();
  CurrentDate.setDate(CurrentDate.getDate() + futureDateRange);
  return CurrentDate;
};

const LoginForm: React.FC<ILoginFormProps> = (props: ILoginFormProps): JSX.Element => {
  const { EDGE_IAM } = getMetaData().xlrConfig;

  const classes = loginStyles();
  const { t } = useTranslation();
  const isEmerald = useIsEmeraldSellingService();

  const { alternateButton, submitButton, lockLabel } = props;
  const submitButtonLabel =
    typeof submitButton.label === 'string' ? t(submitButton.label) : submitButton.label;
  const alternateButtonLabel =
    typeof alternateButton.label === 'string' ? t(alternateButton.label) : alternateButton.label;
  const localizedLockLabel = typeof lockLabel === 'string' ? t(lockLabel) : lockLabel;
  const lockedUser = LocalStorage.getLockUser();

  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });
  const [isActiveTransaction, setIsActiveTransaction] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());
  const [selectedTime, setSelectedTime] = useState<moment.Moment>(moment());

  const usernameInputRef = useRef<HTMLInputElement>(null);
  const didMountRef = useRef<boolean>(false);

  const limitDate = getDateLimit();
  const handleClickShowPassword = (): void => {
    setShowPassword({
      ...showPassword,
      displayPassword: !showPassword.displayPassword,
    });
  };

  useEffect((): void => {
    XLRLogger.debug({
      message: 'Login view ready',
      source: 'login-form.tsx',
    });
  }, []);
  const onDateChange = (date): void => {
    if (date && date.isValid()) {
      setSelectedDate(moment(date));
      props.setErrors({
        ...props.errors,
        Date: '',
      });
      if (
        props.setLoginDate &&
        moment(date).isSameOrAfter(moment(), 'day') &&
        moment(date).isSameOrBefore(moment(limitDate), 'day')
      ) {
        props.setLoginDate(moment(date).format('YYYY-MM-DD'));
        if (moment(date).isAfter(moment(), 'day')) {
          props.setErrors({
            ...props.errors,
            Time: '',
            Date: '',
          });
        } else if (moment(selectedTime).isBefore(moment(), 'minutes')) {
          props.setErrors({
            ...props.errors,
            Time: 'Please enter a future time in the format HH:MM',
          });
        }
      } else {
        props.setErrors({
          ...props.errors,
          Date: `Enter a date between today and ${moment(limitDate).format('MM/DD/YYYY')}`,
        });
      }
    } else {
      setSelectedDate(moment(date));
      props.setErrors({
        ...props.errors,
        Date: 'Please enter a valid date in the format MM/DD/YYYY',
      });
    }
  };

  const onTimeChange = (time): void => {
    if (time && time.isValid()) {
      setSelectedTime(moment(time));
      props.setErrors({
        ...props.errors,
        Time: '',
      });
      if (props.setLoginTime) {
        if (moment(selectedDate).isAfter(moment(), 'day')) {
          props.setLoginTime(moment(time).format('HH:mm:ss'));
        } else if (moment(time).isSameOrAfter(moment(), 'minute')) {
          props.setLoginTime(moment(time).format('HH:mm:ss'));
          if (moment(selectedTime).isSame(moment(), 'day')) {
            props.setErrors({
              ...props.errors,
              Time: '',
              Date: '',
            });
          }
        } else {
          props.setErrors({
            ...props.errors,
            Time: 'Please enter a future time in the format HH:MM tt',
          });
        }
      }
    } else {
      setSelectedTime(moment(time));
      props.setErrors({
        ...props.errors,
        Time: 'Please enter a valid time in the format HH:MM tt',
      });
    }
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = e.target.id;
    const value = e.target.value;
    switch (id) {
      case 'userId':
        props.setUser({
          ...props.user,
          userName: value,
          barcode: undefined,
          trainingMode: props.trainingMode,
        });
        break;
      case 'password':
        props.setUser({
          ...props.user,
          password: value,
        });
        break;
    }
  };

  const clockLogo = <img src={timeIcon} alt='clock-logo' className='MuiSvgIcon-root' />;

  useEffect((): void => {
    const updateSessionWarning = async (): Promise<void> => {
      if (didMountRef.current) {
        if (!isEmerald) {
          await getLatestTransaction();
        }
      } else {
        didMountRef.current = true;
      }
      const { transactionId } = store.getState().transaction;
      setIsActiveTransaction(!!transactionId);
    };
    updateSessionWarning();
  }, [isEmerald, props.trainingMode]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!lockedUser) {
        usernameInputRef.current?.focus();
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [props.trainingMode, lockedUser]);

  const isSmallerDisplay = useMediaQuery('(width:1024px) and (height: 768px)');
  return (
    <Grid container item direction='column' sm={7}>
      <Grid
        item
        container
        direction='row'
        justifyContent='center'
        className={props.trainingMode ? classes.topIconPanelTraining : classes.topIconPanel}
      >
        {isActiveTransaction && (
          <Grid item container sm={7} className={classes.topContainer} spacing={2}>
            <Grid item>
              <ReportProblemOutlinedIcon fontSize='large' color='error' />
            </Grid>
            <Grid item>
              <Typography color='textPrimary' variant='h6' data-testid='active-transaction-label'>
                {t('Lock_ActiveTransaction')}
              </Typography>
            </Grid>
          </Grid>
        )}
        {!isActiveTransaction && <Grid item container sm={7} />}
        <Grid item container sm={5} justifyContent={'flex-end'}>
          <LiveWeightArea isLeftHanded={false} isSummaryDisabled={false} isReversed={true} />
        </Grid>
      </Grid>
      <Grid
        item
        container
        className={props.trainingMode ? classes.loginContainerTraining : classes.loginContainer}
      >
        <Grid item sm={props.user.lockout && isSmallerDisplay ? 1 : 2} />

        <Grid container item sm={props.user.lockout && isSmallerDisplay ? 10 : 8}>
          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
            style={{ height: '4.94vh' }}
            className={props.trainingMode && EDGE_IAM ? classes.welcomeTextSpacing : ''}
          >
            <Grid item>
              <Typography
                className={classes.typographytitle}
                display='inline'
                data-testid='login-screen-message'
              >
                {t(getRegisterTouchpointGreetings())}
              </Typography>
              <Grid item xs={1}>
                <Typography className={classes.typographytitlecontainer} />
              </Grid>
            </Grid>
            {props.user.lockout && (
              <Grid item>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item>
                    <SvgIcon className={classes.icon}>
                      <LockOutlinedIcon fontSize='large' />
                    </SvgIcon>
                  </Grid>
                  <Grid item>
                    <Typography variant='h6' color='textPrimary' data-testid='locked-label'>
                      {`${localizedLockLabel} ${lockedUser}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
          {EDGE_IAM ? (
            <div className={classes.iamButtonContainer}>
              <IAMLoginButton handleIAMLogin={props.handleIAMLogin} />
            </div>
          ) : (
            <form
              className={classes.loginform}
              onSubmit={props.onSubmit}
              noValidate
              data-testid='login-form'
            >
              <Grid
                container
                className={props.trainingMode ? classes.formErrorTraining : classes.formError}
              >
                {props.errors.Description && (
                  <Grid item container>
                    <ReportProblemOutlinedIcon classes={{ root: `${classes.erroricon}` }} />
                    <span className={classes.errorMessage} data-testid='FormErrorLabel'>
                      {props.errors.Description}
                    </span>
                  </Grid>
                )}
              </Grid>
              <Grid container>
                <Grid item sm={12} className={classes.registerFieldRegistration}>
                  <CssTextField
                    classes={{
                      root: classes.registerFieldUnderline,
                    }}
                    variant='standard'
                    margin='none'
                    fullWidth
                    inputProps={{
                      'data-testid': 'userid-input',
                      className: 'global-keyboard',
                      'data-custombuttons': 'keyboardWithClearAndNextButton',
                    }}
                    id='userId'
                    label={props.userLabel}
                    name='userId'
                    onChange={onTextChange}
                    value={props.user.userName || ''}
                    autoComplete='off'
                    inputRef={usernameInputRef}
                  />
                </Grid>
                <Grid item sm={12} className={classes.registerFieldRegistration}>
                  <CssTextField
                    classes={{
                      root: classes.registerFieldUnderline,
                    }}
                    id='password'
                    variant='standard'
                    margin='none'
                    fullWidth
                    inputProps={{
                      'data-testid': 'password-input',
                      className: 'global-keyboard',
                      'data-custombuttons': 'keyboardWithClearAndNextButton',
                    }}
                    name='password'
                    label={props.passwordLabel}
                    type={showPassword.displayPassword ? 'text' : 'password'}
                    onChange={onTextChange}
                    value={props.user.password || ''}
                    autoComplete='new-password'
                    data-testid='pin-code'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            className={classes.whiteiconbutton}
                            edge='end'
                            aria-label='Toggle password visibility'
                            onClick={handleClickShowPassword}
                            tabIndex={-1}
                            data-testid='toggle-password-btn'
                          >
                            {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  {props.lockStatus !== userLockStatus.Supervisor &&
                    !props.user.lockout &&
                    props.trainingMode && (
                      <ThemeProvider theme={datetimeTheme}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <KeyboardDatePicker
                            className={classes.dateField}
                            autoOk={true}
                            value={selectedDate}
                            placeholder={'MM/DD/YYYY'}
                            id='date'
                            label='Date'
                            onChange={date => onDateChange(date)}
                            disablePast={true}
                            disableToolbar={true}
                            minDate={new Date()}
                            maxDate={limitDate}
                            format='MM/DD/YYYY'
                            inputProps={{
                              'data-testid': 'date-input',
                            }}
                            variant={'inline'}
                            error={!!props.errors.Date}
                            helperText={props.errors.Date}
                          />
                        </MuiPickersUtilsProvider>
                      </ThemeProvider>
                    )}
                </Grid>
                <Grid item sm={6} className={classes.timeField}>
                  {props.lockStatus !== userLockStatus.Supervisor &&
                    !props.user.lockout &&
                    props.trainingMode && (
                      <ThemeProvider theme={datetimeTheme}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <KeyboardTimePicker
                            autoOk={true}
                            value={selectedTime}
                            placeholder={'HH:MM AM/PM'}
                            id='time'
                            label='Time'
                            variant='inline'
                            onChange={time => onTimeChange(time)}
                            mask={'__:__ _M'}
                            inputProps={{
                              'data-testid': 'time-input',
                            }}
                            error={!!props.errors.Time}
                            helperText={props.errors.Time}
                            keyboardIcon={clockLogo}
                          />
                        </MuiPickersUtilsProvider>
                      </ThemeProvider>
                    )}
                </Grid>
                <Grid
                  item
                  sm={6}
                  className={
                    props.trainingMode ? classes.trainingButtonContainer : classes.buttonContainer
                  }
                >
                  <BaseButton
                    buttonType='white'
                    className={`${classes.cancelbutton} ${classes.buttondefaultfont}`}
                    fullWidth
                    type='reset'
                    onClick={(): void => alternateButton.onClick()}
                    tabIndex={-1}
                    disabled={alternateButton.disabled}
                    data-testid='alternate-btn'
                  >
                    {alternateButtonLabel}
                  </BaseButton>
                </Grid>
                <Grid
                  item
                  sm={6}
                  className={`${classes.submitContainer} ${
                    props.trainingMode ? classes.trainingButtonContainer : classes.buttonContainer
                  }`}
                >
                  <BaseButton
                    buttonType='primary'
                    className={`${classes.bluebutton} ${classes.buttonwhitefont}`}
                    fullWidth
                    type='submit'
                    tabIndex={-1}
                    data-testid='submit-btn'
                    disabled={
                      submitButton.disabled ||
                      !props.user.userName ||
                      !props.user.password ||
                      (props.lockStatus !== userLockStatus.Supervisor &&
                        !props.user.lockout &&
                        props.trainingMode &&
                        !(selectedDate.isValid() && !(props.errors.Date || props.errors.Time)))
                    }
                  >
                    {submitButtonLabel}
                  </BaseButton>
                </Grid>
              </Grid>
            </form>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface ILoginFormProps {
  userLabel: string;
  passwordLabel: string;
  alternateButton: IButton;
  submitButton: IButton;
  errors: ILoginUserErrors;
  handleIAMLogin: (iamUser: ILoginUser) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  lockLabel?: string;
  lockStatus: userLockStatus;
  // Will be removed after Keyboard refactor
  user: ILoginUser;
  setUser: (user: ILoginUser) => void;
  setErrors: (state: ILoginUserErrors) => void;
  trainingMode?: boolean;
  setLoginDate?: (date: string) => void;
  setLoginTime?: (time: string) => void;
}

export default LoginForm;
